/**
 * AuditDatabase - Manejo de base de datos local para auditorías offline
 * Usa IndexedDB para almacenar inventario y resultados de auditoría
 */

const DB_NAME = 'AuditInventoryDB';
const DB_VERSION = 3; // v3: agrega object store 'closures' para cierre dinámico

export class AuditDatabase {
  constructor() {
    this.db = null;
  }

  /**
   * Inicializa la base de datos
   */
  async init() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('Error abriendo IndexedDB:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        console.log('IndexedDB inicializada correctamente');
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        // Crear almacén de inventario
        if (!db.objectStoreNames.contains('inventory')) {
          const inventoryStore = db.createObjectStore('inventory', { keyPath: 'ean' });
          inventoryStore.createIndex('refe', 'refe', { unique: false });
          inventoryStore.createIndex('categoria', 'categoria', { unique: false });
          inventoryStore.createIndex('marca', 'marca', { unique: false });
        }

        // Crear almacén de lotes
        if (!db.objectStoreNames.contains('batches')) {
          const batchStore = db.createObjectStore('batches', { keyPath: 'id' });
          batchStore.createIndex('status', 'status', { unique: false });
          batchStore.createIndex('createdAt', 'createdAt', { unique: false });
        }

        // Crear almacén de auditorías (un registro por producto por lote)
        if (!db.objectStoreNames.contains('audits')) {
          const auditStore = db.createObjectStore('audits', { 
            keyPath: 'id',
            autoIncrement: false 
          });
          auditStore.createIndex('batchId', 'batchId', { unique: false });
          auditStore.createIndex('productId', 'productId', { unique: false });
          auditStore.createIndex('batchProduct', ['batchId', 'productId'], { unique: true });
          auditStore.createIndex('lastUpdate', 'lastUpdate', { unique: false });
        }

        // Crear almacén de configuración
        if (!db.objectStoreNames.contains('config')) {
          db.createObjectStore('config', { keyPath: 'key' });
        }

        // Almacén de cierres de auditoría (audit trail por cada ejecución del cierre)
        if (!db.objectStoreNames.contains('closures')) {
          const closureStore = db.createObjectStore('closures', { keyPath: 'id' });
          closureStore.createIndex('runAt', 'runAt', { unique: false });
          closureStore.createIndex('partial', 'partial', { unique: false });
        }
      };
    });
  }

  /**
   * Guarda el inventario completo
   */
  async saveInventory(inventoryData) {
    if (!this.db) {
      throw new Error('Base de datos no inicializada');
    }
    
    if (!inventoryData || !Array.isArray(inventoryData)) {
      throw new Error('Datos de inventario inválidos');
    }
    
    console.log(`Guardando ${inventoryData.length} productos en IndexedDB...`);
    
    // Primero limpiar el inventario anterior
    await this.clearObjectStore('inventory');

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['inventory', 'config'], 'readwrite');
      const inventoryStore = transaction.objectStore('inventory');
      const configStore = transaction.objectStore('config');
      
      let addedCount = 0;

      // Manejar eventos de la transacción
      transaction.oncomplete = () => {
        console.log(`Inventario guardado: ${addedCount} productos`);
        resolve(addedCount);
      };

      transaction.onerror = () => {
        console.error('Error guardando inventario:', transaction.error);
        reject(transaction.error);
      };

      // Guardar cada item
      inventoryData.forEach(item => {
        try {
          const request = inventoryStore.add(item);
          request.onsuccess = () => {
            addedCount++;
          };
          request.onerror = () => {
            console.error('Error agregando item:', request.error, item);
          };
        } catch (error) {
          console.error('Error procesando item:', error, item);
        }
      });

      // Guardar timestamp de sincronización
      try {
        configStore.put({
          key: 'lastSync',
          value: new Date().toISOString()
        });
      } catch (error) {
        console.error('Error guardando timestamp:', error);
      }
    });
  }

  /**
   * Obtiene todo el inventario
   */
  async getInventory() {
    const transaction = this.db.transaction(['inventory'], 'readonly');
    const store = transaction.objectStore('inventory');
    
    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Busca un producto por EAN o referencia
   */
  async findProduct(searchTerm) {
    const transaction = this.db.transaction(['inventory'], 'readonly');
    const store = transaction.objectStore('inventory');
    
    // Buscar por EAN primero
    const eanRequest = store.get(searchTerm);
    
    return new Promise((resolve, reject) => {
      eanRequest.onsuccess = () => {
        if (eanRequest.result) {
          resolve(eanRequest.result);
        } else {
          // Si no se encuentra por EAN, buscar por referencia
          const index = store.index('refe');
          const refeRequest = index.get(searchTerm);
          
          refeRequest.onsuccess = () => resolve(refeRequest.result);
          refeRequest.onerror = () => reject(refeRequest.error);
        }
      };
      eanRequest.onerror = () => reject(eanRequest.error);
    });
  }

  /**
   * Guarda un lote
   */
  async saveBatch(batch) {
    const transaction = this.db.transaction(['batches'], 'readwrite');
    const store = transaction.objectStore('batches');
    
    return new Promise((resolve, reject) => {
      const request = store.add(batch);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Actualiza un lote
   */
  async updateBatch(batch) {
    const transaction = this.db.transaction(['batches'], 'readwrite');
    const store = transaction.objectStore('batches');
    
    return new Promise((resolve, reject) => {
      const request = store.put(batch);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Obtiene todos los lotes
   */
  async getBatches() {
    const transaction = this.db.transaction(['batches'], 'readonly');
    const store = transaction.objectStore('batches');
    
    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Elimina un lote
   */
  async deleteBatch(batchId) {
    const transaction = this.db.transaction(['batches', 'audits'], 'readwrite');
    const batchStore = transaction.objectStore('batches');
    const auditStore = transaction.objectStore('audits');
    
    // Eliminar el lote
    batchStore.delete(batchId);
    
    // Eliminar todas las auditorías asociadas
    const index = auditStore.index('batchId');
    const request = index.openCursor(IDBKeyRange.only(batchId));
    
    return new Promise((resolve, reject) => {
      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          auditStore.delete(cursor.primaryKey);
          cursor.continue();
        } else {
          resolve();
        }
      };
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Guarda o actualiza una auditoría (un registro por producto por lote)
   */
  async saveOrUpdateAudit(auditData) {
    const transaction = this.db.transaction(['audits'], 'readwrite');
    const store = transaction.objectStore('audits');
    const index = store.index('batchProduct');
    
    return new Promise((resolve, reject) => {
      // Crear ID único basado en batchId y productId
      auditData.id = `${auditData.batchId}-${auditData.productId}`;
      
      // Intentar obtener el registro existente usando el índice
      const getRequest = index.get([auditData.batchId, auditData.productId]);
      
      getRequest.onsuccess = () => {
        const existingAudit = getRequest.result;
        
        if (existingAudit) {
          // Actualizar el registro existente
          auditData.countedQuantity = existingAudit.countedQuantity + (auditData.quantityToAdd || 1);
          auditData.scanCount = (existingAudit.scanCount || 1) + 1;
        } else {
          // Crear nuevo registro
          auditData.countedQuantity = auditData.quantityToAdd || 1;
          auditData.scanCount = 1;
        }
        
        auditData.lastUpdate = new Date();
        
        const putRequest = store.put(auditData);
        putRequest.onsuccess = () => resolve(auditData);
        putRequest.onerror = () => reject(putRequest.error);
      };
      
      getRequest.onerror = () => reject(getRequest.error);
    });
  }

  /**
   * Obtiene una auditoría específica
   */
  async getAudit(batchId, productId) {
    const transaction = this.db.transaction(['audits'], 'readonly');
    const store = transaction.objectStore('audits');
    const index = store.index('batchProduct');
    
    return new Promise((resolve, reject) => {
      const request = index.get([batchId, productId]);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Actualiza una auditoría existente
   */
  async updateAudit(auditData) {
    const transaction = this.db.transaction(['audits'], 'readwrite');
    const store = transaction.objectStore('audits');
    
    // Asegurar que tiene ID
    auditData.id = auditData.id || `${auditData.batchId}-${auditData.productId}`;
    auditData.lastUpdate = new Date();
    
    return new Promise((resolve, reject) => {
      const request = store.put(auditData);
      request.onsuccess = () => resolve(auditData);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Elimina una auditoría
   */
  async deleteAudit(batchId, productId) {
    const transaction = this.db.transaction(['audits'], 'readwrite');
    const store = transaction.objectStore('audits');
    
    const id = `${batchId}-${productId}`;
    
    return new Promise((resolve, reject) => {
      const request = store.delete(id);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Obtiene todas las auditorías
   */
  async getAudits() {
    const transaction = this.db.transaction(['audits'], 'readonly');
    const store = transaction.objectStore('audits');
    
    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Obtiene auditorías por lote
   */
  async getAuditsByBatch(batchId) {
    const transaction = this.db.transaction(['audits'], 'readonly');
    const store = transaction.objectStore('audits');
    const index = store.index('batchId');
    
    return new Promise((resolve, reject) => {
      const request = index.getAll(batchId);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Obtiene la fecha de última sincronización
   */
  async getLastSync() {
    const transaction = this.db.transaction(['config'], 'readonly');
    const store = transaction.objectStore('config');
    
    return new Promise((resolve, reject) => {
      const request = store.get('lastSync');
      request.onsuccess = () => {
        const result = request.result;
        resolve(result ? result.value : null);
      };
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Marca lotes como sincronizados
   */
  async markAsSynced(batches) {
    const transaction = this.db.transaction(['batches'], 'readwrite');
    const store = transaction.objectStore('batches');
    
    const promises = batches.map(batch => {
      batch.synced = true;
      batch.syncedAt = new Date();
      return store.put(batch);
    });
    
    return Promise.all(promises);
  }

  /**
   * Limpia un almacén específico
   */
  async clearObjectStore(storeName) {
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      
      const request = store.clear();
      
      request.onsuccess = () => {
        console.log(`Almacén ${storeName} limpiado`);
        resolve();
      };
      
      request.onerror = () => {
        console.error(`Error limpiando ${storeName}:`, request.error);
        reject(request.error);
      };
      
      transaction.oncomplete = () => {
        console.log(`Transacción de limpieza de ${storeName} completada`);
      };
      
      transaction.onerror = () => {
        console.error(`Error en transacción de ${storeName}:`, transaction.error);
      };
    });
  }

  /**
   * Limpia toda la base de datos
   */
  async clearAll() {
    const storeNames = ['inventory', 'batches', 'audits', 'config', 'closures'];
    const promises = storeNames.map(storeName => this.clearObjectStore(storeName));
    return Promise.all(promises);
  }

  /**
   * Persiste un cierre de auditoría. Si existe (mismo id) lo reemplaza.
   * Sirve para writes incrementales durante la ejecución del cierre
   * (recovery cuando una de las dos llamadas put_stock falla).
   */
  async saveClosure(closure) {
    const transaction = this.db.transaction(['closures'], 'readwrite');
    const store = transaction.objectStore('closures');
    return new Promise((resolve, reject) => {
      const request = store.put(closure);
      request.onsuccess = () => resolve(closure);
      request.onerror = () => reject(request.error);
    });
  }

  async getClosures() {
    const transaction = this.db.transaction(['closures'], 'readonly');
    const store = transaction.objectStore('closures');
    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => {
        const list = request.result || [];
        list.sort((a, b) => new Date(b.runAt) - new Date(a.runAt));
        resolve(list);
      };
      request.onerror = () => reject(request.error);
    });
  }

  async getClosureById(id) {
    const transaction = this.db.transaction(['closures'], 'readonly');
    const store = transaction.objectStore('closures');
    return new Promise((resolve, reject) => {
      const request = store.get(id);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Marca múltiples lotes como auditados (status='audited') con referencia
   * al cierre que los consumió. Operación atómica en una sola transacción.
   */
  async markBatchesAsAudited(batchIds, closureId) {
    if (!Array.isArray(batchIds) || batchIds.length === 0) return [];
    const transaction = this.db.transaction(['batches'], 'readwrite');
    const store = transaction.objectStore('batches');
    const auditedAt = new Date();

    return new Promise((resolve, reject) => {
      const updated = [];
      let pending = batchIds.length;

      transaction.onerror = () => reject(transaction.error);
      transaction.oncomplete = () => resolve(updated);

      for (const id of batchIds) {
        const getReq = store.get(id);
        getReq.onsuccess = () => {
          const batch = getReq.result;
          if (batch) {
            batch.status = 'audited';
            batch.auditedAt = auditedAt;
            batch.closureId = closureId;
            const putReq = store.put(batch);
            putReq.onsuccess = () => {
              updated.push(batch);
              if (--pending === 0) { /* transaction.oncomplete resuelve */ }
            };
            putReq.onerror = () => reject(putReq.error);
          } else if (--pending === 0) {
            /* sigue */
          }
        };
        getReq.onerror = () => reject(getReq.error);
      }
    });
  }

  /**
   * Obtiene estadísticas de la base de datos
   */
  async getStats() {
    const transaction = this.db.transaction(['inventory', 'batches', 'audits'], 'readonly');
    
    const inventoryCount = await this.getCount(transaction.objectStore('inventory'));
    const batchCount = await this.getCount(transaction.objectStore('batches'));
    const auditCount = await this.getCount(transaction.objectStore('audits'));
    
    return {
      inventory: inventoryCount,
      batches: batchCount,
      audits: auditCount
    };
  }

  /**
   * Cuenta registros en un almacén
   */
  getCount(store) {
    return new Promise((resolve, reject) => {
      const request = store.count();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Exporta todos los datos para backup
   */
  async exportData() {
    const inventory = await this.getInventory();
    const batches = await this.getBatches();
    const audits = await this.getAudits();
    const lastSync = await this.getLastSync();
    
    return {
      inventory,
      batches,
      audits,
      lastSync,
      exportDate: new Date().toISOString()
    };
  }

  /**
   * Importa datos desde un backup
   */
  async importData(data) {
    await this.clearAll();
    
    if (data.inventory) {
      await this.saveInventory(data.inventory);
    }
    
    if (data.batches) {
      const transaction = this.db.transaction(['batches'], 'readwrite');
      const store = transaction.objectStore('batches');
      data.batches.forEach(batch => store.add(batch));
    }
    
    if (data.audits) {
      const transaction = this.db.transaction(['audits'], 'readwrite');
      const store = transaction.objectStore('audits');
      data.audits.forEach(audit => store.add(audit));
    }
  }
}

// Exportar una instancia singleton
export default new AuditDatabase();
